import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Header from "../components/Layout/Header";
import Sidebar from "../components/Layout/Sidebar";
import { server } from "../server";

const resultTabs = [
  { id: "all", label: "All" },
  { id: "products", label: "Products" },
  { id: "shops", label: "Shops" },
  { id: "users", label: "Users" },
];

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const scoreMatch = (value, query) => {
  const text = normalizeText(value);
  const normalizedQuery = normalizeText(query);

  if (!text || !normalizedQuery) {
    return 0;
  }

  if (text === normalizedQuery) {
    return 100;
  }

  if (text.startsWith(normalizedQuery)) {
    return 75;
  }

  if (text.includes(normalizedQuery)) {
    return 50;
  }

  return 10;
};

const getImageUrl = (item) => item?.images?.[0]?.url || item?.avatar?.url || "https://via.placeholder.com/80";

const buildResults = ({ products = [], shops = [], users = [] }, query) => [
  ...products.map((product) => ({
    id: product._id,
    type: "products",
    label: "Product",
    title: product.name,
    subtitle: product.shop?.name || product.brand || "Product",
    image: getImageUrl(product),
    href: `/product/${product._id}`,
    score: Math.max(scoreMatch(product.name, query), scoreMatch(product.shop?.name, query), scoreMatch(product.brand, query)),
  })),
  ...shops.map((shop) => ({
    id: shop._id,
    type: "shops",
    label: "Shop",
    title: shop.name,
    subtitle: shop.handle ? `@${shop.handle}` : "Shop profile",
    image: getImageUrl(shop),
    href: `/shop/${shop.handle}`,
    score: Math.max(scoreMatch(shop.name, query), scoreMatch(shop.handle, query)) + 8,
  })),
  ...users.map((user) => ({
    id: user._id,
    type: "users",
    label: "User",
    title: user.name,
    subtitle: user.handle ? `@${user.handle}` : "Member profile",
    image: getImageUrl(user),
    href: `/user/${user.handle}`,
    score: Math.max(scoreMatch(user.name, query), scoreMatch(user.handle, query)) + 4,
  })),
].sort((first, second) => second.score - first.score || first.title.localeCompare(second.title));

const SearchResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("q") || "";
  const activeType = searchParams.get("type") || "all";
  const [draftQuery, setDraftQuery] = useState(query);
  const [results, setResults] = useState({ products: [], users: [], shops: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setDraftQuery(query);
  }, [query]);

  useEffect(() => {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      setResults({ products: [], users: [], shops: [] });
      setError("");
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setError("");

    axios.get(`${server}/search/search`, { params: { q: trimmedQuery, limit: 40 } })
      .then((response) => {
        if (!isMounted) {
          return;
        }

        if (response.data?.success) {
          setResults({
            products: Array.isArray(response.data.products) ? response.data.products : [],
            users: Array.isArray(response.data.users) ? response.data.users : [],
            shops: Array.isArray(response.data.shops) ? response.data.shops : [],
          });
        } else {
          setResults({ products: [], users: [], shops: [] });
        }
      })
      .catch(() => {
        if (isMounted) {
          setError("Search is unavailable right now.");
          setResults({ products: [], users: [], shops: [] });
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [query]);

  const rankedResults = useMemo(() => buildResults(results, query), [results, query]);
  const filteredResults = activeType === "all" ? rankedResults : rankedResults.filter((item) => item.type === activeType);
  const counts = {
    all: rankedResults.length,
    products: rankedResults.filter((item) => item.type === "products").length,
    shops: rankedResults.filter((item) => item.type === "shops").length,
    users: rankedResults.filter((item) => item.type === "users").length,
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmedQuery = draftQuery.trim();

    if (!trimmedQuery) {
      return;
    }

    setSearchParams({ q: trimmedQuery, type: activeType === "all" ? "all" : activeType });
  };

  const handleTabClick = (type) => {
    setSearchParams({ q: query, type });
  };

  return (
    <div className="search-results-page profile-page">
      <Header />
      <Sidebar />
      <main className="search-results-shell">
        <section className="search-results-hero">
          <div>
            <span>Search</span>
            <h1>{query ? `Results for ${query}` : "Search MATRIX"}</h1>
            <p>Most relevant products, shops, and members are ranked first.</p>
          </div>
          <form className="search-results-form" onSubmit={handleSubmit}>
            <input
              type="search"
              value={draftQuery}
              onChange={(event) => setDraftQuery(event.target.value)}
              placeholder="Search products, shops, and people"
            />
            <button type="submit">Search</button>
          </form>
        </section>

        <nav className="search-results-filters" aria-label="Search filters">
          {resultTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={activeType === tab.id ? "active" : ""}
              onClick={() => handleTabClick(tab.id)}
              disabled={!query.trim()}
            >
              <span>{tab.label}</span>
              <strong>{counts[tab.id]}</strong>
            </button>
          ))}
        </nav>

        <section className="search-results-list" aria-label="Search results">
          {isLoading && <p className="search-results-state">Searching...</p>}
          {error && <p className="search-results-state search-results-error">{error}</p>}
          {!isLoading && !error && query.trim() && filteredResults.length === 0 && (
            <p className="search-results-state">No results found.</p>
          )}
          {!query.trim() && <p className="search-results-state">Enter a search to find products, shops, and members.</p>}
          {!isLoading && !error && filteredResults.map((item) => (
            <Link to={item.href} className="search-results-card" key={`${item.type}-${item.id}`} onClick={() => navigate(item.href)}>
              <img src={item.image} alt="" />
              <div>
                <span>{item.label}</span>
                <h2>{item.title}</h2>
                <p>{item.subtitle}</p>
              </div>
            </Link>
          ))}
        </section>
      </main>
    </div>
  );
};

export default SearchResultsPage;
